import React, { Fragment, useEffect, useState } from 'react';
import {
  Row,
  Table,
  Button,
  Col,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
  Container,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter
} from 'reactstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { DateRangePicker } from 'react-date-range';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender
} from '@tanstack/react-table';

import { rankItem } from '@tanstack/match-sorter-utils';
import JobListGlobalFilter from './GlobalSearchFilter';

// Column Filter
const Filter = ({ column }) => {
  const columnFilterValue = column.getFilterValue();

  return (
    <>
      <DebouncedInput
        type="text"
        value={columnFilterValue ?? ''}
        onChange={(value) => column.setFilterValue(value)}
        placeholder="Search..."
        className="w-36 border shadow rounded"
        list={column.id + 'list'}
      />
      <div className="h-1" />
    </>
  );
};

Filter.propTypes = {
  column: PropTypes.shape({
    getFilterValue: PropTypes.func.isRequired,
    setFilterValue: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired
  }).isRequired
};

// Global Filter
const DebouncedInput = ({ value: initialValue, onChange, debounce = 500, ...props }) => {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [debounce, onChange, value]);

  return (
    <div>
      {/* <i className="mdi mdi-magnify position-absolute input-icon mt-2 " style={{color:'gray'}}></i> */}
      <input
        {...props}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        style={{ marginLeft: 5, width: '30vw' }}
      />
    </div>
  );
};

DebouncedInput.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  debounce: PropTypes.number
};

const TableContainer = ({
  buttonStyle,
  columns,
  data: rawData,
  tableClass,
  theadClass,
  divClassName,
  isBordered,
  isPagination,
  isGlobalFilter,
  paginationWrapper,
  SearchPlaceholder,
  pagination,
  // buttonClass,
  // buttonName,
  isAddButton,
  isCustomPageSize,
  handleUserClick,
  isJobListGlobalFilter,
  isServerPagination = false,
  onPageChange,
  currentPage,
  next,
  prev,
  totalPages,
  pageSize,
  setPageSize,
  setSearchTerm,
  searchTerm,
  serverSearching,
  pageSizeOptions = [10, 20, 30, 40, 50],
  allowFilters,
  setStartDate,
  setEndDate,
  setFiletrValue,
  filterValue,
  selectedRowIds,
  setSelectedRowIds,
  isPaidDropdownOpen,
  setIsPaidDropdownOpen,
  bulkLoader,
  values,
  changeStatus,
  sendEmail,
  canSendFeedbackEmail = true,
  tourId,
  showStatusBar,
  totalAppointment,
  confirmendAppointment,
  completedAppointment,
  pendingAppointment,
  showExportButton,
  ExportComponent,
  localSearchTerm,
  showDate,
  startDate,
  endDate,
  allowCustomDateRange
}) => {
  // Guard against null/undefined data (e.g. a failed/expired-session API
  // response that resolves without throwing) so this renders an empty table
  // instead of crashing every page that embeds it.
  const data = Array.isArray(rawData) ? rawData : [];
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState(localSearchTerm);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [dateRange, setDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);

  const fuzzyFilter = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);
    addMeta({ itemRank });
    return itemRank.passed;
  };
  useEffect(() => {
    setGlobalFilter(localSearchTerm);
  }, [localSearchTerm]);
  const table = isServerPagination
    ? useReactTable({
        columns,
        data,
        filterFns: { fuzzy: fuzzyFilter },
        state: {
          columnFilters,
          globalFilter,
          pagination: { pageIndex: 0, pageSize: pageSize }
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel()
      })
    : useReactTable({
        columns,
        data,
        filterFns: { fuzzy: fuzzyFilter },
        state: {
          columnFilters,
          globalFilter
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: fuzzyFilter,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel()
      });

  const {
    getHeaderGroups,
    getRowModel,
    getCanPreviousPage,
    getCanNextPage,
    getPageOptions,
    setPageIndex,
    nextPage,
    previousPage,
    getState
  } = table;

  // const changePage = () => {
  //   table.setPageIndex((old) => old + 1);
  // };
  console.log(getState);
  function getDateRange(option) {
    const today = new Date();
    const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const endOfDay = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      23,
      59,
      59,
      999
    );

    const startOfWeek = new Date(startOfDay);
    startOfWeek.setDate(startOfWeek.getDate() - (startOfWeek.getDay() || 7) + 1);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(endOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    // ✅ Month boundaries
    const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

    switch (option) {
      case 'All':
        return { startDate: '', endDate: '' };

      case 'Day':
        return {
          startDate: formatDate(startOfDay),
          endDate: formatDate(endOfDay)
        };

      case 'Yesterday': {
        const yesterdayStart = new Date(startOfDay);
        yesterdayStart.setDate(yesterdayStart.getDate() - 1);
        const yesterdayEnd = new Date(endOfDay);
        yesterdayEnd.setDate(yesterdayEnd.getDate() - 1);
        return {
          startDate: formatDate(yesterdayStart),
          endDate: formatDate(yesterdayEnd)
        };
      }

      case 'Tomorrow': {
        const tomorrowStart = new Date(startOfDay);
        tomorrowStart.setDate(tomorrowStart.getDate() + 1);
        const tomorrowEnd = new Date(endOfDay);
        tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
        return {
          startDate: formatDate(tomorrowStart),
          endDate: formatDate(tomorrowEnd)
        };
      }

      case 'Week':
        return {
          startDate: formatDate(startOfWeek),
          endDate: formatDate(endOfWeek)
        };

      // ✅ New Month filter
      case 'Month':
        return {
          startDate: formatDate(startOfMonth),
          endDate: formatDate(endOfMonth)
        };

      default:
        throw new Error('Invalid option');
    }
  }

  const handleDateFilter = () => {
    setFiletrValue('Custom');
    setStartDate(formatDate(dateRange[0]?.startDate));
    setEndDate(formatDate(dateRange[0]?.endDate));
    setModalOpen(false);
  };
  const formatFilterLabel = (filterValue, startDate, endDate) => {
    if (!startDate) return '';

    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;

    const monthFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      year: 'numeric'
    });

    const dayFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    const shortDayFormatter = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    });

    // Month → "February 2025"
    if (filterValue === 'Month') {
      return monthFormatter.format(start);
    }

    // Week → "Mar 3 – Mar 9, 2025"
    if (filterValue === 'Week') {
      return `${shortDayFormatter.format(start)} – ${shortDayFormatter.format(end)}, ${start.getFullYear()}`;
    }

    // Day → "March 5, 2025"
    return dayFormatter.format(start);
  };

  const formatDate = (date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}T${String(date.getHours()).padStart(2, '0')}:${String(
      date.getMinutes()
    ).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}.${String(
      date.getMilliseconds()
    ).padStart(3, '0')}`;

  const handlePrevious = () => {
    const today = new Date(startDate);

    if (filterValue === 'Month') {
      const prevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const start = new Date(prevMonth.getFullYear(), prevMonth.getMonth(), 1, 0, 0, 0);
      const end = new Date(prevMonth.getFullYear(), prevMonth.getMonth() + 1, 0, 23, 59, 59);

      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }

    if (filterValue === 'Week') {
      const start = new Date(today);
      start.setDate(today.getDate() - (today.getDay() || 7) - 6);
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);

      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }

    if (filterValue === 'Day' || !filterValue) {
      const prevDay = new Date(today);
      prevDay.setDate(today.getDate() - 1);

      const start = new Date(prevDay.setHours(0, 0, 0, 0));
      const end = new Date(prevDay.setHours(23, 59, 59, 999));

      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  };
  const handleNext = () => {
    const today = new Date(startDate);

    if (filterValue === 'Month') {
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const start = new Date(nextMonth.getFullYear(), nextMonth.getMonth(), 1, 0, 0, 0);
      const end = new Date(nextMonth.getFullYear(), nextMonth.getMonth() + 1, 0, 23, 59, 59);

      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }

    if (filterValue === 'Week') {
      const start = new Date(today);
      start.setDate(today.getDate() + (7 - (today.getDay() || 7)) + 1);
      start.setHours(0, 0, 0, 0);

      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      end.setHours(23, 59, 59, 999);

      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }

    if (filterValue === 'Day' || !filterValue) {
      const nextDay = new Date(today);
      nextDay.setDate(today.getDate() + 1);

      const start = new Date(nextDay.setHours(0, 0, 0, 0));
      const end = new Date(nextDay.setHours(23, 59, 59, 999));

      setStartDate(formatDate(start));
      setEndDate(formatDate(end));
    }
  };
  const isTodayActive = () => {
    if (!startDate) return true;

    const today = new Date();
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;

    // DAY
    if (filterValue === 'Day' || !filterValue) {
      return start.toDateString() === today.toDateString();
    }

    // WEEK
    if (filterValue === 'Week') {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - (today.getDay() || 7) + 1);
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      endOfWeek.setHours(23, 59, 59, 999);

      return start.getTime() === startOfWeek.getTime() && end.getTime() === endOfWeek.getTime();
    }

    // MONTH
    if (filterValue === 'Month') {
      return start.getMonth() === today.getMonth() && start.getFullYear() === today.getFullYear();
    }

    return false;
  };
  const handleToday = () => {
    const option = ['Day', 'Week', 'Month'].includes(filterValue) ? filterValue : 'Day';
    const date = getDateRange(option);
    setFiletrValue(option);
    setStartDate(date.startDate);
    setEndDate(date.endDate);
  };

  return (
    <Fragment>
      {/* {showStatusBar && (
        <Container fluid className="status-bar-container">
          <Row className="gx-4 gy-1 px-2 py-2">
            {[
              { label: 'Total Appointments', status: '', count: totalAppointment },
              { label: 'Pending Appointments', status: 'Pending', count: pendingAppointment },
              {
                label: 'Confirmed Appointments',
                status: 'Confirmed',
                count: confirmendAppointment
              },
              { label: 'Completed Appointments', status: 'Complete', count: completedAppointment }
            ].map(({ label, status, count }) => {
              const tooltipId = `tooltip-${label.replace(/\s+/g, '-')}`;
              return (
                <Col
                  key={status || 'total'}
                  sm={3}
                  className={`status-box ${selectedStatus === status ? 'selected-box' : ''}`}
                  onClick={() => {
                    setSelectedStatus(status);
                    setSearchTerm(status);
                  }}>
                  <p className="status-label m-0">
                    {label}
                    <span id={tooltipId} className="mx-2">
                      <i className="bx bx-info-circle info-icon" />
                    </span>
                    <UncontrolledTooltip placement="right" target={tooltipId}>
                      Help text
                    </UncontrolledTooltip>
                  </p>
                  <p className="status-count m-0">{count}</p>
                </Col>
              );
            })}
          </Row>
        </Container>
      )} */}
      <Row className="p-0 m-0 d-none d-md-flex">
        <Col sm={7} className="d-flex align-items-center justify-content-start gap-2">
          {showDate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Label */}
              <span style={{ fontSize: 16, fontWeight: '500', color: '#000' }}>
                {formatFilterLabel(filterValue, startDate, endDate)}
              </span>
              <div className="btn-group" style={{ marginLeft: 20, marginRight: 20 }}>
                <button
                  type="button"
                  title="Previous day"
                  className=" btn "
                  onClick={handlePrevious}
                >
                  <span className="fa fa-chevron-left" role="img"></span>
                </button>
                <button type="button" title="Previous day" className=" btn " onClick={handleNext}>
                  <span className="fa fa-chevron-right" role="img"></span>
                </button>
              </div>
              <button
                type="button"
                title="Today"
                onClick={handleToday}
                disabled={isTodayActive()}
                style={{
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: '500',
                  padding: '10px 24px 10px 24px',
                  marginLeft: 8
                }}
                className={`btn ${isTodayActive() ? 'btn-dark text-light' : 'btn-dark'}`}
              >
                Today
              </button>
            </div>
          )}

          {showExportButton && ExportComponent}
          {selectedRowIds &&
            Object.values(selectedRowIds).filter((row) => row.isChecked).length > 0 &&
            canSendFeedbackEmail && (
              <div className="text-sm-end">
                <button
                  type="button"
                  style={{
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: '500',
                    padding: '10px 24px 10px 24px',
                    border: '0px',
                    background: '#000',
                    color: '#fff'
                  }}
                  //   className={'btn bg-primary addContact-modal mb-2 px-4 mt-1'}
                  //   style={buttonStyle}
                  disabled={bulkLoader}
                  onClick={sendEmail}
                >
                  {/* <i className="mdi mdi-plus me-1"></i>  */}

                  {bulkLoader ? 'loading...' : 'Send Feedback Email'}
                </button>
              </div>
            )}
          {selectedRowIds &&
            Object.values(selectedRowIds).filter((row) => row.isChecked).length > 0 && (
              <Dropdown
                isOpen={isPaidDropdownOpen}
                onClick={(e) => console.log(e.target.value, 'e2')}
                onChange={(e) => console.log(e, 'e')}
                toggle={() => {
                  setIsPaidDropdownOpen(!isPaidDropdownOpen);
                }}
              >
                <DropdownToggle
                  // tag="button"
                  //  className={`btn bg-primary addContact-modal mb-2 px-4 mt-1`}
                  style={{
                    borderRadius: 6,
                    fontSize: 13,
                    fontWeight: '500',
                    padding: '10px 24px 10px 24px',
                    border: '0px',
                    background: '#000',
                    color: '#fff'
                  }}
                  className="btn text-white"
                  disabled={bulkLoader}
                >
                  {bulkLoader ? 'loading...' : 'Change Status'}{' '}
                  <i className="mdi mdi-chevron-down" />
                </DropdownToggle>
                <DropdownMenu className="text-start">
                  {values?.map((item) => {
                    return (
                      <DropdownItem key={item} onClick={() => changeStatus(item)}>
                        {item}
                      </DropdownItem>
                    );
                  })}
                </DropdownMenu>
              </Dropdown>
            )}

          {isAddButton && (
            <div className="text-sm-end">
              <Button
                type="button"
                className={'btn bg-primary addContact-modal mb-2 px-4 mt-1'}
                style={buttonStyle}
                id={tourId}
                onClick={handleUserClick}
              >
                {/* <i className="mdi mdi-plus me-1"></i>  */}
                {'New'}
              </Button>
            </div>
          )}
          {isJobListGlobalFilter && <JobListGlobalFilter setGlobalFilter={setGlobalFilter} />}
        </Col>
        <Col sm={5} className="d-flex align-items-center justify-content-end p-0">
          {allowFilters && (
            <div className="d-flex">
              <div className="d-flex">
                {(() => {
                  const viewMap = {
                    Day: 'timeGridDay',
                    Week: 'timeGridWeek',
                    Month: 'dayGridMonth'
                  };

                  const reverseViewMap = {
                    timeGridDay: 'Day',
                    timeGridWeek: 'Week',
                    dayGridMonth: 'Month'
                  };

                  // ✅ get default from localStorage
                  const savedView = localStorage.getItem('calendarView');
                  const defaultOption = reverseViewMap[savedView] || 'Day';

                  return ['Month', 'Week', 'Day'].map((option) => (
                    <button
                      key={option}
                      onClick={() => {
                        // ✅ update localStorage
                        localStorage.setItem('calendarView', viewMap[option]);

                        const date = getDateRange(option);
                        setFiletrValue(option);
                        setStartDate(date.startDate);
                        setEndDate(date.endDate);
                      }}
                      className={`border ${
                        (filterValue ?? defaultOption) === option
                          ? 'bg-primary text-white'
                          : 'defaultBg text-dark'
                      }`}
                      style={{
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: '500',
                        padding: '10px 24px 10px 24px',
                        marginLeft: 8
                      }}
                    >
                      {option}
                    </button>
                  ));
                })()}
                {allowCustomDateRange && (
                  <button
                    type="button"
                    title="Pick a custom date range"
                    onClick={() => setModalOpen(true)}
                    className={`border ${
                      filterValue === 'Custom' ? 'bg-primary text-white' : 'defaultBg text-dark'
                    }`}
                    style={{
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: '500',
                      padding: '10px 24px 10px 24px',
                      marginLeft: 8
                    }}
                  >
                    Custom
                  </button>
                )}
              </div>
            </div>
          )}
        </Col>
        {/* <Col sm={4}></Col> */}
      </Row>
      <div
        className="d-flex align-items-center justify-content-between p-0 m-0 "
        style={{ height: selectedRowIds ? 20 : 0 }}
      >
        {/* Left Section */}
        <div className="d-flex align-items-center ">
          {selectedRowIds &&
            Object.values(selectedRowIds).filter((row) => row.isChecked).length > 0 && (
              <div className="d-flex " style={{ marginLeft: 15 }}>
                <div className="m-0 p-0 d-flex align-items-end ml-2 ">
                  <p className="m-0 p-0">
                    <i
                      className="fas fa-times-circle m-0 p-0"
                      style={{
                        marginRight: '8px',
                        color: 'red',
                        cursor: 'pointer'
                      }}
                      onClick={() => setSelectedRowIds({})}
                    ></i>
                    <strong className="m-0 p-0">&nbsp;Selected Rows:</strong>{' '}
                    {Object.values(selectedRowIds).filter((row) => row.isChecked).length}
                  </p>
                </div>
              </div>
            )}
        </div>

        {/* Right Section */}
      </div>

      {/* selectedRowIds is check if bulk selection feature is enbaled or not */}
      {/* selectedRowIds &&Object.values(selectedRowIds).filter((row) => row.isChecked).length > 0  */}

      <div className={divClassName || 'table-responsive'}>
        <Table hover className={tableClass} bordered={isBordered}>
          <thead className={[...theadClass, 'table-dark']}>
            {getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ color: '#000', fontSize: 13, fontWeight: 700 }}
                    className={header.column.columnDef.enableSorting ? 'sorting sorting_desc' : ''}
                  >
                    {header.isPlaceholder ? null : (
                      <Fragment>
                        <div
                          {...{
                            className: header.column.getCanSort()
                              ? 'cursor-pointer select-none'
                              : '',
                            onClick: header.column.getToggleSortingHandler()
                          }}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {{
                            asc: '',
                            desc: ''
                          }[header.column.getIsSorted()] ?? null}
                        </div>
                        {header.column.getCanFilter() ? (
                          <div>
                            <Filter column={header.column} />
                          </div>
                        ) : null}
                      </Fragment>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {getRowModel().rows.length > 0 ? (
              getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} style={{ fontSize: 13, fontWeight: 400, color: '#000' }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={table.getAllColumns().length}
                  style={{ textAlign: 'center', padding: '10px' }}
                >
                  Nothing to Show
                  <br />
                  <span className="text-muted">{`We couldn’t find any information for your selection.`}</span>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {isPagination && (
        <Row className="d-flex align-items-center justify-content-between">
          {isCustomPageSize && (
            <Col sm={12} md={2} className="d-flex align-items-center ">
              <select
                className="form-select pageSize mb-2"
                value={table.getState().pagination.pageSize}
                onChange={(e) => {
                  const newPageSize = Number(e.target.value);
                  if (isServerPagination) {
                    setPageSize(newPageSize);
                    table.setPageSize(newPageSize);
                  } else {
                    table.setPageSize(newPageSize);
                  }
                }}
                style={{
                  border: '0px',
                  width: '55%',
                  fontSize: 13,
                  color: '#000',
                  fontWeight: 500
                }}
              >
                {pageSizeOptions.map((pageSize) => (
                  <option key={pageSize} value={pageSize}>
                    Show {pageSize}
                  </option>
                ))}
              </select>
            </Col>
          )}
          {/* <Col sm={12} md={3} className="d-flex justify-content-center ">
            {!isServerPagination && (
              <div className="dataTables_info">
                Showing {table.getState().pagination.pageSize} of {data.length} Results
              </div>
            )}
          </Col> */}
          <Col sm={12} md={4} className="d-flex justify-content-end ">
            <div className={paginationWrapper}>
              <ul className={pagination}>
                <li
                  className={`paginate_button page-item previous ${
                    isServerPagination
                      ? prev == null
                        ? 'disabled'
                        : ''
                      : !getCanPreviousPage()
                        ? 'disabled'
                        : ''
                  }`}
                >
                  <Link
                    to="#"
                    className="page-link"
                    onClick={() => {
                      if (isServerPagination) {
                        onPageChange(currentPage - 1);
                      } else {
                        previousPage();
                      }
                    }}
                  >
                    <i className="mdi mdi-chevron-left" style={{ fontSize: 20, color: '#000' }}></i>
                  </Link>
                </li>
                {!isServerPagination ? (
                  getPageOptions().map((item, key) => (
                    <li
                      key={key}
                      className={`paginate_button page-item ${
                        table.getState().pagination.pageIndex === item ? 'active' : ''
                      }`}
                    >
                      <Link to="#" className="page-link" onClick={() => setPageIndex(item)}>
                        {item + 1}
                      </Link>
                    </li>
                  ))
                ) : (
                  <Pagination
                    totalPages={totalPages}
                    currentPage={currentPage}
                    onPageChange={onPageChange}
                  />
                )}
                <li
                  className={`paginate_button page-item next ${
                    isServerPagination
                      ? next == null
                        ? 'disabled'
                        : ''
                      : getCanNextPage()
                        ? ''
                        : 'disabled'
                  }`}
                >
                  <Link
                    to="#"
                    className="page-link"
                    onClick={() => {
                      if (isServerPagination) {
                        onPageChange(currentPage + 1);
                      } else {
                        nextPage();
                      }
                    }}
                  >
                    <i
                      className="mdi mdi-chevron-right"
                      style={{ fontSize: 20, color: '#000' }}
                    ></i>
                  </Link>
                </li>
              </ul>
            </div>
          </Col>
        </Row>
      )}
      {/* Date Range Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="xl">
        <ModalHeader toggle={() => setModalOpen(!modalOpen)}>Filter by Date Range</ModalHeader>
        <ModalBody>
          <DateRangePicker
            editableDateInputs={true}
            onChange={(item) => setDateRange([item.selection])}
            moveRangeOnFirstSelection={false}
            ranges={dateRange}
            months={2}
            direction="horizontal"
            rangeColors={['#000']}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleDateFilter}>
            Apply
          </Button>{' '}
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

TableContainer.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      Header: PropTypes.string,
      accessor: PropTypes.string
      // Add any additional column properties here
    })
  ).isRequired,
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  tableClass: PropTypes.string,
  theadClass: PropTypes.string,
  divClassName: PropTypes.string,
  isBordered: PropTypes.bool,
  isPagination: PropTypes.bool,
  isGlobalFilter: PropTypes.bool,
  paginationWrapper: PropTypes.string,
  SearchPlaceholder: PropTypes.string,
  pagination: PropTypes.string,
  // buttonClass: PropTypes.string,
  // buttonName: PropTypes.string,
  isAddButton: PropTypes.bool,
  isCustomPageSize: PropTypes.bool,
  handleUserClick: PropTypes.func,
  isJobListGlobalFilter: PropTypes.bool,
  buttonStyle: PropTypes.object,
  isServerPagination: PropTypes.bool,
  onPageChange: PropTypes.func,

  currentPage: PropTypes.any,
  next: PropTypes.any,
  prev: PropTypes.any,
  totalPages: PropTypes.any,
  pageSize: PropTypes.any,
  setPageSize: PropTypes.any,
  setSearchTerm: PropTypes.any,
  searchTerm: PropTypes.any,
  serverSearching: PropTypes.any,
  pageSizeOptions: PropTypes.any,
  allowFilters: PropTypes.any,
  setStartDate: PropTypes.any,
  setEndDate: PropTypes.any,
  filterValue: PropTypes.any,
  setFiletrValue: PropTypes.any,
  selectedRowIds: PropTypes.any,
  setSelectedRowIds: PropTypes.any,
  isPaidDropdownOpen: PropTypes.any,
  setIsPaidDropdownOpen: PropTypes.any,
  bulkLoader: PropTypes.any,
  values: PropTypes.any,
  changeStatus: PropTypes.any,
  sendEmail: PropTypes.any,
  canSendFeedbackEmail: PropTypes.bool,
  tourId: PropTypes.any,
  showStatusBar: PropTypes.any,
  totalAppointment: PropTypes.any,
  confirmendAppointment: PropTypes.any,
  completedAppointment: PropTypes.any,
  pendingAppointment: PropTypes.any,
  showExportButton: PropTypes.any,
  exportComponent: PropTypes.any,
  showDate: PropTypes.any,
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  allowCustomDateRange: PropTypes.bool
};

const Pagination = ({ totalPages, currentPage, onPageChange }) => {
  Pagination.propTypes = {
    totalPages: PropTypes.any,
    currentPage: PropTypes.any,
    onPageChange: PropTypes.any
  };
  const getPageNumbers = () => {
    const delta = 2; // Number of pages around the current page
    const range = [];
    const rangeWithDots = [];
    let lastPage = null;

    // Generate a range of pages
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 || // Always show the first page
        i === totalPages || // Always show the last page
        (i >= currentPage - delta && i <= currentPage + delta) // Pages around the current page
      ) {
        range.push(i);
      }
    }

    // Add "..." for skipped ranges
    range.forEach((page) => {
      if (lastPage !== null && page - lastPage > 1) {
        rangeWithDots.push('...');
      }
      rangeWithDots.push(page);
      lastPage = page;
    });

    return rangeWithDots;
  };

  const pages = getPageNumbers();

  return (
    <ul className="pagination">
      {/* Previous Button */}
      {/* <li className={`paginate_button page-item ${currentPage === 1 ? "disabled" : ""}`}>
        <Link to="#" className="page-link" onClick={() => onPageChange(currentPage - 1)}>
          Previous
        </Link>
      </li> */}

      {/* Page Numbers */}
      {pages.map((page, index) => (
        <li
          key={index}
          className={`paginate_button page-item ${
            page === currentPage ? 'active' : page === '...' ? 'disabled' : ''
          }`}
        >
          {page === '...' ? (
            <span className="page-link">...</span>
          ) : (
            <Link
              to="#"
              className="page-link"
              onClick={() => onPageChange(page)}
              style={{ fontSize: '13px', fontWeight: 500 }}
            >
              {page}
            </Link>
          )}
        </li>
      ))}

      {/* Next Button */}
      {/* <li className={`paginate_button page-item ${currentPage === totalPages ? "disabled" : ""}`}>
        <Link to="#" className="page-link" onClick={() => onPageChange(currentPage + 1)}>
          Next
        </Link>
      </li> */}
    </ul>
  );
};

export default TableContainer;
